import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import Table from '../components/Table';
import './WhatsAppEvents.css';

const WHATSAPP_COLLECTION = 'whatsapp_notifications';
const PUSH_COLLECTION = 'notifications';
const FETCH_BATCH_SIZE = 100;

// Booking-lifecycle event keys, mirroring UlloorStyles/functions/whatsapp.js TEMPLATES.
const EVENT_TYPE_LABELS = {
  booking_confirmed: 'Booking Confirmed (Customer)',
  booking_received: 'New Booking (Salon)',
  booking_completed: 'Service Completed (Customer)',
  booking_cancelled_customer: 'Cancelled by Salon → Customer',
  booking_cancelled_salon: 'Cancelled by Customer → Salon',
  booking_reschedule_requested: 'Reschedule Requested (Customer)',
  booking_rescheduled_salon: 'Rescheduled (Salon)'
};

const STATUS_META = {
  failed_to_send: { label: 'Failed to Send', className: 'status-cancelled' },
  sent: { label: 'Sent', className: 'status-sent' },
  delivered: { label: 'Delivered', className: 'status-confirmed' },
  read: { label: 'Read', className: 'status-read' },
  delivery_failed: { label: 'Delivery Failed', className: 'status-cancelled' }
};

const CHANNEL_TABS = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'push', label: 'Push' },
  { key: 'all', label: 'Unified' }
];

function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts?.toDate === 'function') return ts.toDate().getTime();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function formatDateTime(ts) {
  const ms = toMillis(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function maskPhone(phone) {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 6) return phone;
  return `${digits.slice(0, 2)}••••••${digits.slice(-4)}`;
}

function startOfDayTimestamp(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
}

function endOfDayTimestamp(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
}

function deriveWhatsAppStatusKey(d) {
  if (!d.sent) return 'failed_to_send';
  if (d.deliveryStatus === 'delivered') return 'delivered';
  if (d.deliveryStatus === 'read') return 'read';
  if (d.deliveryStatus === 'failed') return 'delivery_failed';
  return 'sent';
}

function derivePushStatusKey(d) {
  return d.sent ? 'sent' : 'failed_to_send';
}

// Both collections mirror each other (see UlloorStyles/functions/whatsapp.js and
// the push-notification writers in functions/index.js) but aren't identical shapes
// (templateName/phone/messageId/deliveryStatus vs title/body/data) — normalize into
// one row shape so the table/filters/status badges stay channel-agnostic.
function normalizeWhatsAppDoc(docSnap) {
  const d = docSnap.data();
  const statusKey = deriveWhatsAppStatusKey(d);
  return {
    id: docSnap.id,
    channel: 'whatsapp',
    type: d.type,
    typeLabel: EVENT_TYPE_LABELS[d.type] || d.type || 'Unknown event',
    recipientType: d.recipientType,
    recipientId: d.recipientId,
    bookingId: d.bookingId,
    phone: d.phone,
    summary: d.templateName || '—',
    statusKey,
    statusMeta: STATUS_META[statusKey],
    createdAt: d.createdAt,
    createdAtMs: toMillis(d.createdAt),
    raw: d
  };
}

function normalizePushDoc(docSnap) {
  const d = docSnap.data();
  const statusKey = derivePushStatusKey(d);
  return {
    id: docSnap.id,
    channel: 'push',
    type: d.type,
    typeLabel: EVENT_TYPE_LABELS[d.type] || d.type || 'Unknown event',
    recipientType: d.recipientType,
    recipientId: d.recipientId,
    bookingId: d.bookingId,
    phone: null,
    summary: d.title || '—',
    statusKey,
    statusMeta: STATUS_META[statusKey],
    createdAt: d.createdAt,
    createdAtMs: toMillis(d.createdAt),
    raw: d
  };
}

function WhatsAppEvents() {
  const [channel, setChannel] = useState('whatsapp'); // 'whatsapp' | 'push' | 'all'
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const cursorRef = useRef({ whatsapp: null, push: null });

  const [selectedRow, setSelectedRow] = useState(null);
  const [revealedIds, setRevealedIds] = useState(() => new Set());

  const [statsRangeDays, setStatsRangeDays] = useState(7);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchChannelBatch = useCallback(
    async (channelName, cursorDoc) => {
      const collectionName = channelName === 'push' ? PUSH_COLLECTION : WHATSAPP_COLLECTION;
      const constraints = [orderBy('createdAt', 'desc')];
      const fromTs = startOfDayTimestamp(dateFrom);
      const toTs = endOfDayTimestamp(dateTo);
      if (fromTs) constraints.push(where('createdAt', '>=', fromTs));
      if (toTs) constraints.push(where('createdAt', '<=', toTs));
      if (cursorDoc) constraints.push(startAfter(cursorDoc));
      constraints.push(limit(FETCH_BATCH_SIZE));

      const snap = await getDocs(query(collection(db, collectionName), ...constraints));
      const normalize = channelName === 'push' ? normalizePushDoc : normalizeWhatsAppDoc;
      return { docs: snap.docs, rows: snap.docs.map(normalize) };
    },
    [dateFrom, dateTo]
  );

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      cursorRef.current = { whatsapp: null, push: null };

      if (channel === 'all') {
        // Best-effort unified view: latest batch per channel, merged client-side.
        // Deep cursor-merging across two independent Firestore streams isn't worth
        // the complexity here — switch to a single channel for full history.
        const [wa, push] = await Promise.all([
          fetchChannelBatch('whatsapp', null),
          fetchChannelBatch('push', null)
        ]);
        cursorRef.current.whatsapp = wa.docs[wa.docs.length - 1] || null;
        cursorRef.current.push = push.docs[push.docs.length - 1] || null;
        const merged = [...wa.rows, ...push.rows].sort((a, b) => b.createdAtMs - a.createdAtMs);
        setRows(merged);
        setHasMore(false);
      } else {
        const result = await fetchChannelBatch(channel, null);
        cursorRef.current[channel] = result.docs[result.docs.length - 1] || null;
        setRows(result.rows);
        setHasMore(result.docs.length === FETCH_BATCH_SIZE);
      }
    } catch (err) {
      setError('Failed to load WhatsApp events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [channel, fetchChannelBatch]);

  const loadMore = useCallback(async () => {
    if (channel === 'all' || !hasMore) return;
    try {
      setLoading(true);
      const result = await fetchChannelBatch(channel, cursorRef.current[channel]);
      cursorRef.current[channel] = result.docs[result.docs.length - 1] || cursorRef.current[channel];
      setRows((prev) => [...prev, ...result.rows]);
      setHasMore(result.docs.length === FETCH_BATCH_SIZE);
    } catch (err) {
      setError('Failed to load more events.');
    } finally {
      setLoading(false);
    }
  }, [channel, fetchChannelBatch, hasMore]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const fetchTotalCount = useCallback(async () => {
    if (channel === 'all') {
      setTotalCount(null);
      return;
    }
    try {
      const collectionName = channel === 'push' ? PUSH_COLLECTION : WHATSAPP_COLLECTION;
      const constraints = [];
      const fromTs = startOfDayTimestamp(dateFrom);
      const toTs = endOfDayTimestamp(dateTo);
      if (fromTs) constraints.push(where('createdAt', '>=', fromTs));
      if (toTs) constraints.push(where('createdAt', '<=', toTs));
      const snap = await getCountFromServer(query(collection(db, collectionName), ...constraints));
      setTotalCount(snap.data().count || 0);
    } catch (err) {
      setTotalCount(null);
    }
  }, [channel, dateFrom, dateTo]);

  useEffect(() => {
    fetchTotalCount();
  }, [fetchTotalCount]);

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const cutoff = Timestamp.fromDate(new Date(Date.now() - statsRangeDays * 24 * 60 * 60 * 1000));
      const base = collection(db, WHATSAPP_COLLECTION);
      const [totalSnap, failedSnap, deliveredSnap, readSnap] = await Promise.all([
        getCountFromServer(query(base, where('createdAt', '>=', cutoff))),
        getCountFromServer(query(base, where('sent', '==', false), where('createdAt', '>=', cutoff))),
        getCountFromServer(query(base, where('deliveryStatus', '==', 'delivered'), where('createdAt', '>=', cutoff))),
        getCountFromServer(query(base, where('deliveryStatus', '==', 'read'), where('createdAt', '>=', cutoff)))
      ]);
      const total = totalSnap.data().count || 0;
      const failed = failedSnap.data().count || 0;
      const delivered = deliveredSnap.data().count || 0;
      const read = readSnap.data().count || 0;
      setStats({
        total,
        failed,
        failedRate: total ? Math.round((failed / total) * 100) : 0,
        deliveredRate: total ? Math.round((delivered / total) * 100) : 0,
        readRate: total ? Math.round((read / total) * 100) : 0
      });
    } catch (err) {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [statsRangeDays]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const toggleReveal = (id) => {
    setRevealedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Status/event-type/recipient/search filters apply client-side over the loaded
  // batch(es) — Firestore composite indexes only cover the date-range + channel
  // query itself and the stats aggregates above.
  const filteredRows = useMemo(() => {
    const qDigits = searchText.replace(/\D/g, '');
    const qLower = searchText.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.statusKey !== statusFilter) return false;
      if (eventTypeFilter !== 'all' && r.type !== eventTypeFilter) return false;
      if (recipientFilter !== 'all' && r.recipientType !== recipientFilter) return false;
      if (qLower) {
        const phoneDigits = String(r.phone || '').replace(/\D/g, '');
        const matchesPhone = qDigits.length >= 4 && phoneDigits.includes(qDigits);
        const matchesBookingId = String(r.bookingId || '').toLowerCase().includes(qLower);
        if (!matchesPhone && !matchesBookingId) return false;
      }
      return true;
    });
  }, [rows, statusFilter, eventTypeFilter, recipientFilter, searchText]);

  const columns = [
    {
      key: 'createdAt',
      title: 'Time',
      render: (_, row) => formatDateTime(row.createdAt)
    },
    {
      key: 'channel',
      title: 'Channel',
      render: (_, row) => (row.channel === 'whatsapp' ? '💬 WhatsApp' : '🔔 Push')
    },
    {
      key: 'typeLabel',
      title: 'Event'
    },
    {
      key: 'recipientType',
      title: 'Recipient',
      render: (_, row) => (
        <div className="wae-recipient-cell">
          <span className="wae-recipient-type">
            {row.recipientType === 'salon_owner' ? 'Salon' : 'Customer'}
          </span>
          {row.channel === 'whatsapp' && (
            <span className="wae-phone">
              {revealedIds.has(row.id) ? row.phone || '—' : maskPhone(row.phone)}
              {row.phone && (
                <button type="button" className="wae-reveal-btn" onClick={() => toggleReveal(row.id)}>
                  {revealedIds.has(row.id) ? 'Hide' : 'Show'}
                </button>
              )}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'summary',
      title: 'Template / Title'
    },
    {
      key: 'statusMeta',
      title: 'Status',
      render: (_, row) => (
        <span className={`status-badge ${row.statusMeta.className}`}>{row.statusMeta.label}</span>
      )
    },
    {
      key: 'bookingId',
      title: 'Booking Ref',
      className: 'wae-booking-col',
      render: (_, row) => (row.bookingId ? <span className="wae-booking-id">{row.bookingId}</span> : '—')
    },
    {
      key: 'details',
      title: '',
      render: (_, row) => (
        <button type="button" className="wae-details-btn" onClick={() => setSelectedRow(row)}>
          Details
        </button>
      )
    }
  ];

  return (
    <div className="whatsapp-events-page">
      <div className="page-header">
        <div className="header-info">
          <h2>WhatsApp Events</h2>
          <p>
            {totalCount != null ? `${totalCount} total · ` : ''}
            Showing {filteredRows.length} of {rows.length} loaded
          </p>
        </div>
        <button onClick={loadEvents} className="refresh-btn" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="wae-stats-row">
        <div className="wae-stats-header">
          <h3>Overview</h3>
          <div className="wae-stats-range">
            {[1, 7, 30].map((d) => (
              <button
                key={d}
                className={`filter-btn ${statsRangeDays === d ? 'active' : ''}`}
                onClick={() => setStatsRangeDays(d)}
              >
                {d === 1 ? 'Today' : `${d} days`}
              </button>
            ))}
          </div>
        </div>
        {statsLoading ? (
          <p className="wae-stats-loading">Loading stats…</p>
        ) : stats ? (
          <div className="wae-stats-cards">
            <div className="wae-stat-card">
              <span className="wae-stat-value">{stats.total}</span>
              <span className="wae-stat-label">Total WhatsApp sends</span>
            </div>
            <div className="wae-stat-card wae-stat-card--bad">
              <span className="wae-stat-value">{stats.failed}</span>
              <span className="wae-stat-label">Failed to send ({stats.failedRate}%)</span>
            </div>
            <div className="wae-stat-card wae-stat-card--good">
              <span className="wae-stat-value">{stats.deliveredRate}%</span>
              <span className="wae-stat-label">Delivered</span>
            </div>
            <div className="wae-stat-card">
              <span className="wae-stat-value">{stats.readRate}%</span>
              <span className="wae-stat-label">Read</span>
            </div>
          </div>
        ) : (
          <p className="wae-stats-loading">Stats unavailable.</p>
        )}
      </div>

      <div className="filters-container">
        <div className="tabs-container">
          {CHANNEL_TABS.map((c) => (
            <button
              key={c.key}
              className={`tab-btn ${channel === c.key ? 'active' : ''}`}
              onClick={() => setChannel(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="filters">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All statuses
          </button>
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <button
              key={key}
              className={`filter-btn ${statusFilter === key ? 'active' : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {meta.label}
            </button>
          ))}
        </div>

        <div className="wae-filter-row">
          <label className="month-filter-field">
            <span className="month-filter-label">Event type</span>
            <select
              className="month-filter-select"
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
            >
              <option value="all">All events</option>
              {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label className="month-filter-field">
            <span className="month-filter-label">Recipient</span>
            <select
              className="month-filter-select"
              value={recipientFilter}
              onChange={(e) => setRecipientFilter(e.target.value)}
            >
              <option value="all">All recipients</option>
              <option value="customer">Customer</option>
              <option value="salon_owner">Salon</option>
            </select>
          </label>

          <label className="month-filter-field">
            <span className="month-filter-label">From</span>
            <input
              type="date"
              className="month-filter-select"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>

          <label className="month-filter-field">
            <span className="month-filter-label">To</span>
            <input
              type="date"
              className="month-filter-select"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>

          <label className="month-filter-field wae-search-field">
            <span className="month-filter-label">Search (phone or booking ID)</span>
            <input
              type="text"
              className="month-filter-select"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="9876543210 or booking ID"
            />
          </label>
        </div>
      </div>

      {error ? (
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={loadEvents} className="retry-btn">
            Try Again
          </button>
        </div>
      ) : (
        <div className="table-section">
          <Table
            columns={columns}
            data={filteredRows}
            loading={loading && rows.length === 0}
            emptyMessage="No WhatsApp events found for the selected filters"
          />
          {channel !== 'all' && hasMore && (
            <div className="wae-load-more-row">
              <button className="refresh-btn" onClick={loadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
          {channel === 'all' && (
            <p className="wae-unified-note">
              Unified view shows the latest {FETCH_BATCH_SIZE} events per channel. Switch to WhatsApp or Push
              for full history.
            </p>
          )}
        </div>
      )}

      {selectedRow && (
        <div className="wae-modal-overlay" onClick={() => setSelectedRow(null)}>
          <div className="wae-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="wae-modal-header">
              <h3>{selectedRow.typeLabel}</h3>
              <button type="button" className="wae-modal-close" onClick={() => setSelectedRow(null)}>
                &times;
              </button>
            </div>
            <div className="wae-modal-body">
              <dl className="wae-modal-detail-list">
                <div>
                  <dt>Channel</dt>
                  <dd>{selectedRow.channel === 'whatsapp' ? 'WhatsApp' : 'Push notification'}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    <span className={`status-badge ${selectedRow.statusMeta.className}`}>
                      {selectedRow.statusMeta.label}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Recipient</dt>
                  <dd>
                    {selectedRow.recipientType === 'salon_owner' ? 'Salon' : 'Customer'} ·{' '}
                    {selectedRow.recipientId || '—'}
                  </dd>
                </div>
                {selectedRow.channel === 'whatsapp' && (
                  <>
                    <div>
                      <dt>Phone</dt>
                      <dd>{selectedRow.phone || '—'}</dd>
                    </div>
                    <div>
                      <dt>Template</dt>
                      <dd>{selectedRow.raw.templateName || '—'}</dd>
                    </div>
                    <div>
                      <dt>Message ID</dt>
                      <dd className="wae-mono">{selectedRow.raw.messageId || '—'}</dd>
                    </div>
                  </>
                )}
                <div>
                  <dt>Booking ID</dt>
                  <dd className="wae-mono">{selectedRow.bookingId || '—'}</dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatDateTime(selectedRow.createdAt)}</dd>
                </div>
                {selectedRow.channel === 'whatsapp' && selectedRow.raw.deliveryStatusUpdatedAt && (
                  <div>
                    <dt>Delivery updated</dt>
                    <dd>{formatDateTime(selectedRow.raw.deliveryStatusUpdatedAt)}</dd>
                  </div>
                )}
                {selectedRow.raw.error && (
                  <div>
                    <dt>Send error</dt>
                    <dd className="wae-error-text">{selectedRow.raw.error}</dd>
                  </div>
                )}
                {selectedRow.raw.deliveryError && (
                  <div>
                    <dt>Delivery error</dt>
                    <dd className="wae-error-text">{selectedRow.raw.deliveryError}</dd>
                  </div>
                )}
                {selectedRow.channel === 'push' && (
                  <>
                    <div>
                      <dt>Title</dt>
                      <dd>{selectedRow.raw.title || '—'}</dd>
                    </div>
                    <div>
                      <dt>Body</dt>
                      <dd>{selectedRow.raw.body || '—'}</dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsAppEvents;
